import { useNavigate } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { TextField, Grid, Box, Autocomplete } from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import Checkbox from "@mui/material/Checkbox";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import useTerritory from "hooks/useTerritory";
import useDealer from "hooks/useDealer";
import { useLocation } from "react-router-dom";
const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const initialState = {
  name: "",
  email: "",
  role: "DEALER",
  countryCode: "+91",
  mobile: "",
  bussinessName: "",
  gstNo: "",
  territory: [],
  street: "",
  city: "",
  state: "",
  pincode: "",
  country: "",
};

const DealerForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const dealerId = queryParams.get("id");
  const { getTerritoryOptions } = useTerritory();
  const { getdealerById, loading, updatedealer, createdealer } = useDealer();
  const [territoryOptions, setTerritoryOptions] = useState([]);
  const [formData, setFormData] = useState(initialState);

  const handleSalesChange = (event, value) => {
    setFormData((prevState) => ({
      ...prevState,
      territory: value.map((territory) => territory._id),
    }));
  };

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => {
      if (name.includes(".")) {
        const keys = name.split(".");
        return {
          ...prevState,
          [keys[0]]: {
            ...prevState[keys[0]],
            [keys[1]]: value,
          },
        };
      }
      return {
        ...prevState,
        [name]: value,
      };
    });
  };

  const validateForm = () => {
    let newErrors = {};
    if (!formData.name) newErrors.name = "Name is required";
    if (!formData.email) newErrors.email = "Email is required";
    if (!formData.mobile) newErrors.mobile = "Mobile number is required";
    if (!formData.bussinessName) newErrors.bussinessName = "Business name is required";
    if (!formData.gstNo) newErrors.gstNo = "GST number is required";
    if (!formData.territory) newErrors.territory = "Territory is required";
    if (!formData.street) newErrors.street = "Street is required";
    if (!formData.city) newErrors.city = "City is required";
    if (!formData.state) newErrors.state = "State is required";
    if (!formData.pincode) newErrors.pincode = "Pincode is required";
    if (!formData.country) newErrors.country = "Country is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      if (dealerId) {
        const updateResposne = await updatedealer(dealerId, formData);
        if (updateResposne?.success) {
          setFormData(initialState);
          navigate("/dealer-management");
        } else {
          alert(updateResposne?.message);
        }
        return;
      }
      const createResposne = await createdealer(formData);
      if (createResposne?.success) {
        setFormData(initialState);
        navigate("/dealer-management");
      } else {
        alert(createResposne?.message);
      }
    }
  };

  const fetchTerritoryList = async () => {
    const response = await getTerritoryOptions();
    if (response.success) {
      setTerritoryOptions(response.data);
    }
  };

  const fetchDealer = async () => {
    const response = await getdealerById(dealerId);
    if (response?.success) {
      setFormData({
        name: response.data.userId.name,
        email: response.data.userId.email,
        userType: response.data.userType,
        countryCode: response.data.countryCode,
        mobile: response.data.userId.mobile,
        bussinessName: response.data.bussinessName,
        gstNo: response.data.gstNo,
        territory: response.data.territory.map((territory) => territory._id),
        street: response.data.address.street,
        city: response.data.address.city,
        state: response.data.address.state,
        pincode: response.data.address.pincode,
        country: response.data.address.country,
      });
    }
  };

  useEffect(() => {
    fetchTerritoryList();
    if (dealerId) {
      fetchDealer();
    }
  }, [dealerId]);

  return (
    <Box
      sx={{
        width: "100%",
        background: "white",
        minHeight: "calc(100vh - 120px)",
        p: 4,
        borderRadius: 2,
        boxShadow: 2,
      }}
    >
      <MDTypography variant="h5" align="center" gutterBottom>
        Dealer Form
      </MDTypography>
      <MDTypography variant="caption" color="text" align="center" paragraph>
        Fill out the details below to register/update Dealer.
      </MDTypography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <MDTypography variant="body2" gutterBottom>
            Name
          </MDTypography>
          <TextField
            fullWidth
            name="name"
            value={formData.name}
            onChange={handleChange}
            error={!!errors.name}
            helperText={errors.name}
            required
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <MDTypography variant="body2" gutterBottom>
            Mobile Number
          </MDTypography>
          <TextField
            fullWidth
            name="mobile"
            value={formData.mobile}
            onChange={handleChange}
            error={!!errors.mobile}
            helperText={errors.mobile}
            required
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <MDTypography variant="body2" gutterBottom>
            Business Name
          </MDTypography>
          <TextField
            fullWidth
            name="bussinessName"
            value={formData.bussinessName}
            onChange={handleChange}
            error={!!errors.bussinessName}
            helperText={errors.bussinessName}
            required
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <MDTypography variant="body2" gutterBottom>
            Email
          </MDTypography>
          <TextField
            fullWidth
            name="email"
            value={formData.email}
            onChange={handleChange}
            error={!!errors.email}
            helperText={errors.email}
            required
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <MDTypography variant="body2" gutterBottom>
            GST Number
          </MDTypography>
          <TextField
            fullWidth
            name="gstNo"
            value={formData.gstNo}
            onChange={handleChange}
            error={!!errors.gstNo}
            helperText={errors.gstNo}
            required
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <MDTypography variant="body2" gutterBottom>
            Street
          </MDTypography>
          <TextField
            fullWidth
            name="street"
            value={formData.street}
            onChange={handleChange}
            error={!!errors.street}
            helperText={errors.street}
            required
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <MDTypography variant="body2" gutterBottom>
            City
          </MDTypography>
          <TextField
            fullWidth
            name="city"
            value={formData.city}
            onChange={handleChange}
            error={!!errors.city}
            helperText={errors.city}
            required
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <MDTypography variant="body2" gutterBottom>
            State
          </MDTypography>
          <TextField
            fullWidth
            name="state"
            value={formData.state}
            onChange={handleChange}
            error={!!errors.state}
            helperText={errors.state}
            required
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <MDTypography variant="body2" gutterBottom>
            Pincode
          </MDTypography>
          <TextField
            fullWidth
            name="pincode"
            type="number"
            value={formData.pincode}
            onChange={handleChange}
            error={!!errors.pincode}
            helperText={errors.pincode}
            required
            variant="outlined"
          />
        </Grid>
        {dealerId && (
          <Grid item xs={12} md={6}>
            <MDTypography variant="body2" gutterBottom>
              Territory
            </MDTypography>
            <Autocomplete
              disabled
              multiple
              name="sales"
              value={territoryOptions.filter((territory) =>
                formData.territory.includes(territory._id)
              )}
              onChange={handleSalesChange}
              id="checkboxes-tags-demo"
              options={territoryOptions}
              disableCloseOnSelect
              getOptionLabel={(option) => option?.name}
              renderOption={(props, option, { selected }) => {
                const { ...optionProps } = props;
                return (
                  <li {...optionProps}>
                    <Checkbox
                      icon={icon}
                      checkedIcon={checkedIcon}
                      style={{ marginRight: 8 }}
                      checked={selected}
                    />
                    {option?.name}
                  </li>
                );
              }}
              renderInput={(params) => <TextField {...params} />}
            />
          </Grid>
        )}
        <Grid item xs={12} md={6}>
          <MDTypography variant="body2" gutterBottom>
            Country
          </MDTypography>
          <TextField
            fullWidth
            name="country"
            value={formData.country}
            onChange={handleChange}
            error={!!errors.country}
            helperText={errors.country}
            required
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12}>
          <MDBox>
            <MDButton
              type="submit"
              variant="contained"
              color="dark"
              fullWidth
              sx={{ marginTop: "3rem" }}
              onClick={handleSubmit}
            >
              {loading ? "Submitting..." : dealerId ? "Update Dealer" : "Submit"}
            </MDButton>
          </MDBox>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DealerForm;
